import { useEffect, useRef, useState, useCallback } from "react";
import { RichTextEditor } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import { Box, Text } from "@mantine/core";
import { createClinicalRichTextExtensions } from "../shared/tiptap/clinicalRichTextExtensions";
import { AddendumTypographyControls } from "../shared/tiptap/AddendumTypographyControls";
import { useActiveAbbreviations } from "../../hooks/useActiveAbbreviations";
import {
  ADDENDUM_RICH_TEXT_CLASS,
  ADDENDUM_RICH_TEXT_STYLES,
  CLINICAL_RICH_TEXT_COLORS,
  CLINICAL_RICH_TEXT_PARSE_OPTIONS,
} from "../../utils/clinicalRichText";
import MacroVariableModal, {
  extractMacroVariables,
  replaceMacroVariables,
} from "./MacroVariableModal";
import VariableChip, {
  wrapVariablesAsChips,
  type VariableConfig,
  type VariableFieldConfig,
} from "../shared/tiptap/VariableChip";

export type { VariableConfig, VariableFieldConfig };
export { wrapVariablesAsChips };

export interface DiagnosisMacroItem {
  id: string;
  code: string;
  label: string;
  diagnosisText: string;
  microscopicDescription: string | null;
  category: string | null;
  cptCode: string | null;
  icdCode: string | null;
  icdCodes?: string[];
}

interface RichTextDiagnosisProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  withAsterisk?: boolean;
  placeholder?: string;
  disabled?: boolean;
  minHeight?: number;
  error?: string;
  autoFocus?: boolean;
  variableConfig?: VariableConfig;
  /** Called when a macro is applied so the parent can also set related fields (e.g. microscopicDescription) */
  onMacroApplied?: (macro: DiagnosisMacroItem) => void;
  /** Optional node rendered as a header bar inside the editor box, above the toolbar */
  codeInput?: React.ReactNode;
  /** When true, skip auto-focusing the editor content on mount (so a code input can receive focus instead) */
  suppressEditorFocus?: boolean;
  supplementalTypography?: "schema" | "toolbar";
  paragraphSpacing?: "default" | "single-line";
}

export default function RichTextDiagnosis({
  value,
  onChange,
  label,
  withAsterisk = false,
  placeholder = "Enter text...",
  disabled = false,
  minHeight = 100,
  error,
  autoFocus = false,
  variableConfig,
  onMacroApplied,
  codeInput,
  suppressEditorFocus = false,
  supplementalTypography,
  paragraphSpacing = "default",
}: RichTextDiagnosisProps) {
  const { abbreviations } = useActiveAbbreviations();

  const [macroVarModal, setMacroVarModal] = useState<{
    opened: boolean;
    macro: DiagnosisMacroItem | null;
  }>({ opened: false, macro: null });

  // Store latest callbacks in refs so macro handlers don't need editor in dep array
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const onMacroAppliedRef = useRef(onMacroApplied);
  onMacroAppliedRef.current = onMacroApplied;
  const editorRef = useRef<ReturnType<typeof useEditor>>(null);
  const isUpdatingRef = useRef(false);
  const lastValueRef = useRef(value);
  const lastPropValueRef = useRef(value);

  const editor = useEditor({
    extensions: [
      ...createClinicalRichTextExtensions(placeholder, {
        supplementalTypography: Boolean(supplementalTypography),
      }),
      VariableChip,
    ],
    content: wrapVariablesAsChips(value),
    parseOptions: CLINICAL_RICH_TEXT_PARSE_OPTIONS,
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      if (isUpdatingRef.current) return;
      const html = ed.getHTML();
      if (html !== lastValueRef.current) {
        lastValueRef.current = html;
        lastPropValueRef.current = html;
        onChangeRef.current(html);
      }
    },
    editorProps: {},
  });

  // Keep the editor ref in sync so macro callbacks can access it without deps
  useEffect(() => {
    (editorRef as React.MutableRefObject<typeof editor>).current = editor;
  });

  // Apply a macro's diagnosisText to the editor after variable substitution
  const applyMacroToEditor = useCallback(
    (macro: DiagnosisMacroItem, variables: Record<string, string>) => {
      const resolvedText = replaceMacroVariables(
        macro.diagnosisText || "",
        variables
      );
      const wrappedText = wrapVariablesAsChips(resolvedText);
      const ed = editorRef.current;
      if (ed && !ed.isDestroyed) {
        isUpdatingRef.current = true;
        ed.commands.setContent(wrappedText, {
          emitUpdate: false,
          parseOptions: CLINICAL_RICH_TEXT_PARSE_OPTIONS,
        });
        const html = ed.getHTML();
        lastValueRef.current = html;
        lastPropValueRef.current = html;
        onChangeRef.current(html);
        setTimeout(() => {
          isUpdatingRef.current = false;
        }, 0);
      }
      const resolvedMicro = replaceMacroVariables(
        macro.microscopicDescription || "",
        variables
      );
      onMacroAppliedRef.current?.({
        ...macro,
        microscopicDescription: resolvedMicro,
      });
    },
    []
  );

  // Keep editor.storage.variableChip.config in sync with the variableConfig prop.
  // NodeViews read from storage since they may be mounted outside the React tree.
  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (editor.storage as any).variableChip.config = variableConfig ?? {};
    }
  }, [editor, variableConfig]);

  // Sync abbreviation data into editor.storage.textExpansion.abbreviationMap
  useEffect(() => {
    if (editor && !editor.isDestroyed && abbreviations.length > 0) {
      const map = new Map<string, string>();
      abbreviations.forEach((abbr) => {
        map.set(abbr.shortcode.toLowerCase(), abbr.expansion);
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (editor.storage as any).textExpansion.abbreviationMap = map;
    }
  }, [editor, abbreviations]);

  // Update editor content when value prop changes from parent
  useEffect(() => {
    if (!editor) return;
    if (value === lastPropValueRef.current) return;

    lastPropValueRef.current = value;
    isUpdatingRef.current = true;
    editor.commands.setContent(wrapVariablesAsChips(value || ""), {
      emitUpdate: false,
      parseOptions: CLINICAL_RICH_TEXT_PARSE_OPTIONS,
    });
    lastValueRef.current = editor.getHTML();
    setTimeout(() => {
      isUpdatingRef.current = false;
    }, 0);
  }, [value, editor]);

  // Update editable state when disabled changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [disabled, editor]);

  useEffect(() => {
    if (autoFocus && editor && !disabled && !suppressEditorFocus) {
      editor.commands.focus("end");
    }
  }, [autoFocus, editor, disabled, suppressEditorFocus]);

  return (
    <Box>
      {paragraphSpacing === "single-line" && (
        <style>{ADDENDUM_RICH_TEXT_STYLES}</style>
      )}
      {label && (
        <Text size="sm" fw={500} mb={4}>
          {label}
          {withAsterisk && (
            <Text span c="red" ml={2}>
              *
            </Text>
          )}
        </Text>
      )}
      <RichTextEditor
        editor={editor}
        data-clinical-rich-text-editor
        className={
          paragraphSpacing === "single-line"
            ? ADDENDUM_RICH_TEXT_CLASS
            : undefined
        }
        styles={{
          root: {
            border: error
              ? "1px solid var(--mantine-color-red-6)"
              : "1px solid var(--mantine-color-gray-4)",
            borderRadius: "var(--mantine-radius-sm)",
          },
          content: {
            minHeight,
            backgroundColor: disabled ? "var(--mantine-color-gray-1)" : "white",
          },
          toolbar: {
            borderBottom: "1px solid var(--mantine-color-gray-3)",
          },
        }}
      >
        <RichTextEditor.Toolbar>
          {codeInput && (
            <Box mr={4} style={{ display: "flex", alignItems: "center" }}>
              {codeInput}
            </Box>
          )}

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
            <RichTextEditor.Underline />
          </RichTextEditor.ControlsGroup>

          {supplementalTypography === "toolbar" && (
            <AddendumTypographyControls editor={editor} />
          )}

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.BulletList />
            <RichTextEditor.OrderedList />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Undo />
            <RichTextEditor.Redo />
          </RichTextEditor.ControlsGroup>

          <RichTextEditor.ControlsGroup>
            <RichTextEditor.ColorPicker colors={CLINICAL_RICH_TEXT_COLORS} />
            <RichTextEditor.UnsetColor />
            <RichTextEditor.Highlight />
          </RichTextEditor.ControlsGroup>
        </RichTextEditor.Toolbar>

        <RichTextEditor.Content />
      </RichTextEditor>
      {error && (
        <Text size="xs" c="red" mt={4}>
          {error}
        </Text>
      )}

      {macroVarModal.macro && (
        <MacroVariableModal
          opened={macroVarModal.opened}
          onClose={() => setMacroVarModal({ opened: false, macro: null })}
          onSubmit={(variables) => {
            if (macroVarModal.macro) {
              applyMacroToEditor(macroVarModal.macro, variables);
            }
          }}
          variables={extractMacroVariables(
            (macroVarModal.macro.diagnosisText || "") +
              (macroVarModal.macro.microscopicDescription || "")
          )}
          macroCode={macroVarModal.macro.code}
        />
      )}
    </Box>
  );
}

// Utility to strip HTML tags for plain text display
export function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
}

// Utility to check if content is empty (ignoring HTML tags)
export function isRichTextEmpty(html: string): boolean {
  return stripHtml(html).trim() === "";
}
